<script lang="ts" setup>
import { defineComponent, ref } from 'vue'
import { onBeforeRouteUpdate } from 'vue-router'

import { Background } from '@base/_types/Background'

import BackgroundWrapper from '@base/components/BackgroundWrapper/BackgroundWrapper.vue'
import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import USButton from '@base/components/USButton/USButton.vue'
import USInputText from '@base/components/USInputText/USInputText.vue'

import SplitContent from '@player/_components/SplitContent/SplitContent.vue'

import registerStep1 from '@player/users/assets/register-step-1.png'

import blobBg from '@player/users/assets/register-step-1-blob-bg.svg'

import { usersRouteNames } from '@player/users/router/routeNames'

defineComponent({
  name: 'RegisterStep1'
})

const registerFields = ref({
  firstName: '',
  lastName: '',
  email: ''
})
const firstNameError = ref('')
const lastNameError = ref('')
const emailError = ref('')

const emits = defineEmits(['next', 'previous'])

const bgImage: Background = {
  fullWidth: true,
  overflow: true,
  media: [
    {
      type: 'image',
      position: {
        alignHorizontal: 'left_50',
        alignVertical: 'center',
        centerAnchor: false,
        cover: false,
        foreground: false,
        objectPositionHorizontal: 'center',
        objectPositionVertical: 'center'
      },
      imageSet: {
        mobile: {
          fadeIn: true,
          lazy: true,
          src: blobBg
        }
      }
    }
  ]
}

const reset = () => {
  registerFields.value.firstName = ''
  registerFields.value.lastName = ''
  registerFields.value.email = ''
  firstNameError.value = ''
  lastNameError.value = ''
  emailError.value = ''
}

const handleNextButton = () => {
  if (!registerFields.value.firstName) {
    firstNameError.value = 'Please input your first name'
  } else {
    firstNameError.value = ''
  }

  if (!registerFields.value.lastName) {
    lastNameError.value = 'Please input your last name'
  } else {
    lastNameError.value = ''
  }

  if (!registerFields.value.email || !(/^[^@\s]+@[^@\s]+\.[^@\s]+$/).test(registerFields.value.email)) {
    emailError.value = 'Please enter a valid email address'
  } else {
    emailError.value = ''
  }

  if (firstNameError.value || lastNameError.value || emailError.value) return

  emits('next', registerFields.value)
}

reset()
</script>

<template>
  <div class="register-step-1">
    <BackgroundWrapper :background="bgImage" />
    <SplitContent
      :sticky="true"
      top-offset="var(--player-header-height)"
      class="margin-top--5 margin-bottom--5"
    >
      <template #left>
        <div class="register-step-1__form-wrapper">
          <h1 class="heading--h3 margin-bottom--5">
            Step 1: Let's get to know each other
          </h1>
          <USInputText
            v-model="registerFields.firstName"
            name="firstName"
            heading="First Name"
            class="register-step-1__input"
            input-id="firstName"
            :error="firstNameError"
            :required="true"
            input-type="text"
          />
          <USInputText
            v-model="registerFields.lastName"
            name="lastName"
            heading="Last Name"
            class="register-step-1__input"
            input-id="lastName"
            :error="lastNameError"
            input-type="text"
            :required="true"
          />
          <USInputText
            v-model="registerFields.email"
            name="email"
            heading="Email"
            input-id="email"
            :error="emailError"
            input-type="email"
            :required="true"
          />
          <div class="register-step-1__buttons">
            <div class="register-step-1__button">
              <USButton is="button" label="Next" @click="handleNextButton" />
            </div>
            <div class="register-step-1__button register-step-1__button--right">
              <router-link :to="{ name: usersRouteNames.SignIn }">
                <USButton
                  label="Already have an account? Log in"
                  type="inline"
                />
              </router-link>
            </div>
          </div>
        </div>
      </template>
      <template #right>
        <div class="register-step-1__image-right">
          <ImageWrapper :src="registerStep1" aspect-ratio="1" />
        </div>
      </template>
    </SplitContent>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint' as bp;

.register-step-1 {
  color: var(--color--neutral-a1);

  &__input {
    margin-bottom: var(--spacing--6);
  }

  &__image-right {
    margin-right: auto;
    margin-left: auto;
    max-width: 370px;
    margin-top: var(--spacing--5);

    @include bp.min-width(bp.$breakpoint--7) {
      margin-top: 0;
    }
  }

  &__buttons {
    align-items: center;
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    margin-top: var(--spacing--5);

    @include bp.min-width(bp.$breakpoint--7) {
      flex-flow: row;
      justify-content: space-between;
    }
  }

  &__button {
    margin-top: var(--spacing--6);

    @include bp.min-width(bp.$breakpoint--7) {
      margin-top: 0;
    }

    &--right {
      @include bp.min-width(bp.$breakpoint--7) {
        margin-left: var(--spacing--6);
      }
    }
  }
}

:deep(.split-content__content) {
  flex-flow: column-reverse;

  @media (min-width: bp.$breakpoint--tablet) {
    flex-flow: row wrap;
  }
}
</style>
